// Rich, SEO-focused content for each service detail page (keyed by slug)
export const SERVICE_DETAILS = {
  "forensic-and-critical-remediation": {
    name: "Forensic & Critical Remediation",
    metaTitle: "Forensic Cleaning & Biohazard Remediation | Swan Hill, VIC & NSW",
    metaDescription:
      "IICRC certified forensic cleaning: unattended death, trauma and crime scene clean-up, and biological/biohazard remediation. Discreet, documented, 24/7 across Swan Hill, Victoria & NSW.",
    intro:
      "Discreet, respectful and controlled decontamination for incidents involving blood, bodily fluids or other potentially infectious materials.",
    paragraphs: [
      "Forensic and critical remediation covers unattended death clean-up, trauma and crime scene clean-up, and biological and biohazard decontamination. These situations demand sensitivity, speed and rigorous process — not a standard clean.",
      "Every attendance is handled with compassion for families, tenants and stakeholders, and delivered using controlled, standards based methods aligned with IICRC S540. We contain the affected area, remove contamination at the source, decontaminate, and verify the result with traceable documentation.",
    ],
    bullets: [
      "Unattended and undiscovered death clean-up",
      "Trauma, suicide and crime scene decontamination",
      "Blood and bodily fluid remediation",
      "Odour control and porous-material removal",
      "Full containment and PPE-controlled work",
      "Traceable documentation for a defensible handover",
    ],
    faqs: [
      { q: "Is your work discreet?", a: "Yes. We attend in unmarked vehicles where requested and treat every site and family with the utmost respect and confidentiality." },
      { q: "How quickly can you attend?", a: "We provide 24/7 responsive attendance across our service area and prioritise urgent forensic and trauma situations." },
      { q: "Do you liaise with insurers and agents?", a: "Yes. We provide a clear scope of works, quote and reporting to support insurers, real estate agencies, councils and support services." },
    ],
  },
  "water-damage-restoration": {
    name: "Water Damage Restoration",
    metaTitle: "Water Damage Restoration (IICRC S500) | Swan Hill, VIC & NSW",
    metaDescription:
      "Assessment, extraction, structural drying and restoration of water-affected properties to IICRC S500. Fast response across Swan Hill, regional Victoria & NSW.",
    intro:
      "Assessment, extraction, drying and restoration of water-affected structures — delivered to the IICRC S500 standard.",
    paragraphs: [
      "Water intrusion from storms, burst pipes, appliance failures or flooding can quickly cause structural damage and secondary issues such as mould. Rapid, correct action limits loss and cost.",
      "We assess the category and class of water intrusion, extract standing water, and dry the structure to a documented dry standard using calibrated equipment and moisture monitoring, in line with IICRC S500.",
    ],
    bullets: [
      "Category and class assessment",
      "Water extraction and removal",
      "Structural drying with monitoring",
      "Moisture mapping and documentation",
      "Mould prevention measures",
      "Restoration to pre-loss condition",
    ],
    faqs: [
      { q: "Do you provide moisture readings and reports?", a: "Yes. We document moisture levels throughout drying and provide reporting suitable for insurance claims." },
      { q: "Can you prevent mould after water damage?", a: "Prompt, correct drying is the best defence against mould. Where microbial growth is present we can remediate to IICRC S520." },
      { q: "How fast should water damage be actioned?", a: "As quickly as possible — ideally within 24–48 hours — to limit structural damage and secondary contamination." },
    ],
  },
  "mould-remediation": {
    name: "Mould Remediation",
    metaTitle: "Mould Testing & Remediation (IICRC S520) | Swan Hill, VIC & NSW",
    metaDescription:
      "Assessment-led mould remediation with testing and inspection to IICRC S520. Identify the extent and cause of microbial growth. Servicing Swan Hill, Victoria & NSW.",
    intro:
      "Assessment led remediation, including testing and inspection to understand the extent of microbial growth and the conditions contributing to it.",
    paragraphs: [
      "Mould is a symptom of a moisture problem. Effective remediation requires understanding both the extent of contamination and the conditions driving it — not just cleaning what is visible.",
      "We inspect and test, contain the affected area to prevent cross-contamination, remove contamination at the source, and verify the outcome, aligned with IICRC S520 — protecting occupant health and reducing the chance of recurrence.",
    ],
    bullets: [
      "Testing and moisture inspection",
      "Cause and extent assessment",
      "Containment to prevent spread",
      "Source removal and remediation",
      "Post-remediation verification",
      "Recommendations to prevent recurrence",
    ],
    faqs: [
      { q: "Do you test for mould?", a: "Yes. Our approach is assessment led — we test and inspect to understand the extent and cause before remediating." },
      { q: "Why does mould keep coming back?", a: "Because the underlying moisture source hasn't been resolved. We identify and address the contributing conditions, not just the visible growth." },
      { q: "Is mould a health risk?", a: "It can be, particularly for sensitive occupants. Containment and correct removal protect occupant health during and after works." },
    ],
  },
  "clandestine-laboratory-cleanups": {
    name: "Clandestine Laboratory Clean-ups",
    metaTitle: "Meth Lab & Clandestine Drug Lab Remediation | Swan Hill, VIC & NSW",
    metaDescription:
      "Remediation of meth-affected properties and grow labs, aligned with the Australian Clandestine Drug Laboratory Remediation Guidelines. Swan Hill, Victoria & NSW.",
    intro:
      "Remediation of meth affected properties and grow labs, delivered in alignment with the Australian Clandestine Drug Laboratory Remediation Guidelines.",
    paragraphs: [
      "Properties used to manufacture or store illicit drugs can carry hazardous chemical residues that pose serious health risks to occupants and require specialist decontamination.",
      "We undertake these higher-risk works with appropriate assessment, containment and decontamination, delivered in alignment with the Australian Clandestine Drug Laboratory Remediation Guidelines, supported by sampling and clearance documentation.",
    ],
    bullets: [
      "Meth (methamphetamine) contamination remediation",
      "Grow-house / cannabis lab decontamination",
      "Assessment and sampling",
      "Containment and hazardous residue removal",
      "Clearance testing coordination",
      "Defensible documentation and reporting",
    ],
    faqs: [
      { q: "How do you know if a property is contaminated?", a: "Through assessment and sampling. We can coordinate testing to determine contamination levels before and after remediation." },
      { q: "Which guidelines do you follow?", a: "We align with the Australian Clandestine Drug Laboratory Remediation Guidelines for assessment, decontamination and clearance." },
      { q: "Can you provide clearance documentation?", a: "Yes. We provide traceable documentation to support a defensible, safe handover." },
    ],
  },
  "property-make-safe": {
    name: "Property Make Safe",
    metaTitle: "Property Make Safe & Emergency Stabilisation | Swan Hill, VIC & NSW",
    metaDescription:
      "Immediate make-safe and stabilisation of compromised properties after fire, storm, water or incident. 24/7 emergency response across Swan Hill, Victoria & NSW.",
    intro:
      "Immediate stabilisation and securing of compromised properties following an incident.",
    paragraphs: [
      "After a fire, storm, break-in or serious incident, a property may be unsafe or exposed. Make-safe works stabilise and secure the site to prevent further damage or risk.",
      "We attend rapidly to assess hazards and carry out the make-safe measures required to protect the property and enable the next stage of works, with clear documentation for insurers and owners.",
    ],
    bullets: [
      "Emergency attendance and hazard assessment",
      "Securing and boarding up",
      "Temporary protection from weather",
      "Removal of immediate hazards",
      "Coordination with insurers and trades",
      "Documented scope for next-stage works",
    ],
    faqs: [
      { q: "Do you offer emergency make-safe?", a: "Yes. We provide 24/7 responsive attendance for make-safe and stabilisation across our service area." },
      { q: "Do you work with insurance?", a: "Yes. We document the make-safe scope and provide reporting to support insurance claims." },
    ],
  },
  "strip-out-pack-out-clear-out": {
    name: "Strip-out, Pack-out & Clear-out",
    metaTitle: "Strip-out, Pack-out & Estate Clear-out | Swan Hill, VIC & NSW",
    metaDescription:
      "Contents pack-outs, property strip-outs and estate clear-outs to support safe handover and re-occupancy. Servicing Swan Hill, regional Victoria & NSW.",
    intro:
      "Contents pack outs, property strip outs, and estate clear outs.",
    paragraphs: [
      "Whether preparing a property for restoration, managing an estate, or resetting a site for handover, controlled removal and handling of contents is often required.",
      "We provide contents pack-outs, property strip-outs and estate clear-outs, handling items appropriately and supporting a safe, orderly handover and re-occupancy.",
    ],
    bullets: [
      "Contents pack-out and handling",
      "Property strip-out",
      "Estate and deceased-estate clear-outs",
      "Sorting, disposal and donation coordination",
      "Site reset for restoration or handover",
      "Respectful, documented process",
    ],
    faqs: [
      { q: "Can you handle deceased estates?", a: "Yes. We manage estate clear-outs respectfully and can coordinate donation of usable items where appropriate." },
      { q: "Do you dispose of items responsibly?", a: "Yes. We sort for disposal, recycling and donation, and document the process." },
    ],
  },
  "property-refresh-and-declutter": {
    name: "Property Refresh & Declutter Service",
    metaTitle: "Property Refresh & Declutter Service | Swan Hill, VIC & NSW",
    metaDescription:
      "Structured decluttering, clearance and sanitisation to refresh properties and restore safe, comfortable re-occupancy. Compassionate service across Swan Hill, VIC & NSW.",
    intro:
      "Structured decluttering, clearance and sanitisation to refresh properties and restore safe, comfortable re-occupancy.",
    paragraphs: [
      "Some properties need more than a clean — they need a structured, compassionate reset. Our property refresh and declutter service helps restore safe, comfortable living conditions.",
      "We work methodically and respectfully, decluttering, clearing and sanitising to a standard that supports safe re-occupancy, often in partnership with support services and family members.",
    ],
    bullets: [
      "Structured decluttering and clearance",
      "Deep sanitisation and refresh",
      "Compassionate, respectful approach",
      "Coordination with support services",
      "Safe, comfortable re-occupancy",
      "Discreet, documented process",
    ],
    faqs: [
      { q: "Do you work with support services?", a: "Yes. We regularly partner with community support services and families to achieve safe, dignified outcomes for clients." },
      { q: "Is the service confidential?", a: "Absolutely. We treat every client and property with discretion and respect." },
    ],
  },
};

// Short, shareable URL aliases (e.g. for Google Business Profile / ads).
// Each short path renders the service page and canonicalises to /services/<slug>.
export const ALIAS_TO_SLUG = {
  "forensic-cleaning": "forensic-and-critical-remediation",
  "water-damage": "water-damage-restoration",
  "mould": "mould-remediation",
  "meth-lab": "clandestine-laboratory-cleanups",
  "make-safe": "property-make-safe",
  "strip-out": "strip-out-pack-out-clear-out",
  "declutter": "property-refresh-and-declutter",
};
